// =============================================================================
// MeuDriver — Compare modal for InstructorsPage
// =============================================================================

import { Link } from 'react-router-dom'
import {
  BookOpen,
  DollarSign,
  MapPin,
  Star,
} from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SPECIALTY_LABELS } from '@/lib/constants'
import { formatCurrency, cn, getStudentPrice } from '@/lib/utils'
import type { InstructorWithExtras } from '@/hooks/useInstructors'

interface Props {
  instructors: InstructorWithExtras[]
  open: boolean
  onClose: () => void
}

export function CompareModal({ instructors, open, onClose }: Props) {
  const rows: {
    label: string
    icon: React.ReactNode
    getValue: (i: InstructorWithExtras) => string
  }[] = [
    {
      label: 'Preço/hora',
      icon: <DollarSign className="h-3.5 w-3.5" />,
      getValue: (i) => formatCurrency(getStudentPrice(i.hourly_rate ?? 0)),
    },
    {
      label: 'Avaliação',
      icon: <Star className="h-3.5 w-3.5" />,
      getValue: (i) => (i.rating_avg != null ? `${i.rating_avg.toFixed(1)} ★` : '—'),
    },
    {
      label: 'Total de aulas',
      icon: <BookOpen className="h-3.5 w-3.5" />,
      getValue: (i) => `${(i.total_lessons ?? 0).toLocaleString('pt-BR')} aulas`,
    },
    {
      label: 'Cidade',
      icon: <MapPin className="h-3.5 w-3.5" />,
      getValue: (i) => [i.profile?.city, i.profile?.state].filter(Boolean).join(', ') || '—',
    },
    {
      label: 'Especialidades',
      icon: <Star className="h-3.5 w-3.5" />,
      getValue: (i) => {
        const raw = i.specialties ?? []
        return raw
          .map((s) => SPECIALTY_LABELS[s.specialty] ?? s.specialty)
          .join(', ') || '—'
      },
    },
  ]

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl w-full p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle>Comparar instrutores</DialogTitle>
        </DialogHeader>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="w-32 p-4 text-left text-xs text-muted-foreground font-medium">Critério</th>
                {instructors.map((inst) => {
                  const p = inst.profile
                  const slug = inst.slug ?? inst.id
                  const initials = (p?.full_name ?? 'I')
                    .split(' ')
                    .slice(0, 2)
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                  return (
                    <th key={inst.id} className="p-4 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <Avatar className="h-12 w-12">
                          <AvatarFallback className="bg-brand-100 text-brand-700 font-bold text-sm">{initials}</AvatarFallback>
                          <AvatarFallback>{initials}</AvatarFallback>
                        </Avatar>
                        <p className="text-sm font-semibold text-gray-900 leading-tight">{p?.full_name ?? 'Instrutor'}</p>
                        <Button asChild size="sm" variant="outline" className="h-7 text-xs">
                          <Link to={`/instrutores/${slug}`}>Ver perfil</Link>
                        </Button>
                      </div>
                    </th>
                  )
                })}
              </tr>
            </thead>

            <tbody>
              {rows.map((row, ri) => {
                const values = instructors.map(row.getValue)
                const best = ri === 0
                  ? values.indexOf([...values].sort((a, b) => parseFloat(a.replace(/\D/g, '')) - parseFloat(b.replace(/\D/g, '')))[0])
                  : ri === 1
                  ? values.indexOf([...values].sort((a, b) => parseFloat(b) - parseFloat(a))[0])
                  : ri === 2
                  ? values.indexOf([...values].sort((a, b) => parseInt(b) - parseInt(a))[0])
                  : -1

                return (
                  <tr key={row.label} className={cn('border-b last:border-0', ri % 2 === 0 ? 'bg-gray-50/50' : 'bg-white')}>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                        {row.icon}
                        {row.label}
                      </span>
                    </td>
                    {values.map((val, vi) => (
                      <td key={vi} className={cn(
                        'px-4 py-3 text-center text-sm',
                        vi === best && ri < 3 ? 'font-bold text-brand-600' : 'text-gray-700',
                      )}>
                        {val}
                        {vi === best && ri < 3 && (
                          <span className="ml-1 text-[10px] text-brand-500 font-normal">✓ melhor</span>
                        )}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t flex justify-end">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
